import React, { useState, useEffect } from "react";
import { validateEmail, signupToWaitlist } from "../../utils";
import EmailPopup from "../modals/EmailPopup";
import { Badge, ChevronDownIcon, CheckIcon, ArrowRightIcon } from "../ui";
import { AppPreview, ShareCardInline, SessionSwitchLink } from "../features";
import {
  TYPOGRAPHY,
  BREAKPOINTS,
  TEXT_SHADOWS,
  ANIMATIONS,
} from "../../constants";

const TabletHeroLayout = ({
  windowSize,
  referrals,
  setReferrals,
  hasJoined: initialHasJoined,
  queuePosition,
  onWaitlistSuccess,
  onReferFriend,
  referredBy,
  // Additional props for referral popup
  referralLink,
  referralCount = 0,
  // Session switching (returning user on a friend's link / different email)
  hasExistingSession = false,
  onShowExisting,
  onUseDifferentEmail,
}) => {
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [hasJoined, setHasJoined] = useState(initialHasJoined || false);
  const [showInlineCard, setShowInlineCard] = useState(false);

  // Keep local joined state in sync when the parent switches views
  useEffect(() => {
    setHasJoined(initialHasJoined || false);
    if (!initialHasJoined) setShowInlineCard(false);
  }, [initialHasJoined]);

  const handleShowReferral = () => {
    if (onReferFriend) onReferFriend();
    setShowInlineCard(true);
  };

  // Get responsive typography and spacing based on tablet breakpoints ONLY (768px-1199px)
  const getResponsiveSpecs = () => {
    const width = windowSize?.width || BREAKPOINTS.TABLET_MD; // Default to iPad Air size

    if (width < BREAKPOINTS.SURFACE_DUO) {
      // Very small tablets/large phones (below Surface Duo)
      return {
        titleSize: "2rem", // 32px - Very compact for small screens
        paragraphSize: "1.125rem", // 18px - Compact paragraph
        buttonSize: "1rem", // 16px - Small button text
        titleLineHeight: TYPOGRAPHY.LINE_HEIGHTS.TIGHT,
        paragraphLineHeight: TYPOGRAPHY.LINE_HEIGHTS.NORMAL,
        titleFontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
        paragraphFontWeight: TYPOGRAPHY.WEIGHTS.LIGHT, // 300 - Light weight
        textShadow: TEXT_SHADOWS.HERO_MOBILE_MEDIUM,
        mainPadding: "0 1rem",
        titleMargin: "0.75rem",
        paragraphMargin: "1rem",
        appPreviewMargin: "1rem 0",
        maxTitleWidth: "95%",
        maxParagraphWidth: "90%",
        headerPadding: "0 0.75rem",
        headerTop: "1.25rem", // Increased from 0.75rem for more header spacing
        buttonContainerMaxWidth: "280px",
      };
    } else if (width < BREAKPOINTS.TABLET_MD) {
      // Surface Duo + iPad Mini range (540-833px) - Compact tablet layout
      return {
        titleSize: "2.5rem", // 40px - Further reduced for better balance on iPad Mini
        paragraphSize: "1.25rem", // 20px - Slightly reduced for better proportion
        buttonSize: "1.125rem", // 18px - Reduced from 20px
        titleLineHeight: TYPOGRAPHY.LINE_HEIGHTS.TIGHT, // 1.296 for hero titles
        paragraphLineHeight: TYPOGRAPHY.LINE_HEIGHTS.NORMAL, // 1.5 for readability
        titleFontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD, // 600 - hero emphasis
        paragraphFontWeight: TYPOGRAPHY.WEIGHTS.LIGHT, // 300 - Light weight as specified
        textShadow: TEXT_SHADOWS.HERO_MOBILE_MEDIUM, // Medium shadow for tablets
        mainPadding: "0 1.5rem", // 24px horizontal - reduced
        titleMargin: "1rem", // Reduced for tighter layout
        paragraphMargin: "1.25rem", // Reduced for tighter layout
        appPreviewMargin: "1.25rem 0", // Reduced for tighter layout
        maxTitleWidth: "95%", // Increased width utilization
        maxParagraphWidth: "90%", // Increased width utilization
        headerPadding: "0 1rem", // 16px - reduced header padding
        headerTop: "1.5rem", // Increased header top spacing for better separation
        buttonContainerMaxWidth: "320px", // Smaller button container for mini
      };
    } else if (width < BREAKPOINTS.TABLET_LG) {
      // iPad Air range (834-1023px) - Standard tablet layout
      return {
        titleSize: TYPOGRAPHY.TABLET.MD.HERO_TITLE, // 56px from design system
        paragraphSize: "1.5rem", // 24px - Poppins 24px Regular as specified
        buttonSize: TYPOGRAPHY.TABLET.MD.CTA_BUTTON, // 22px from design system
        titleLineHeight: TYPOGRAPHY.LINE_HEIGHTS.TIGHT, // 1.296 for hero titles
        paragraphLineHeight: TYPOGRAPHY.LINE_HEIGHTS.NORMAL, // 1.5 for readability
        titleFontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD, // 600 - hero emphasis
        paragraphFontWeight: TYPOGRAPHY.WEIGHTS.LIGHT, // 300 - Light weight as specified
        textShadow: TEXT_SHADOWS.HERO_MOBILE_MEDIUM, // Medium shadow for tablets
        mainPadding: "0 2rem", // 32px horizontal
        titleMargin: "1.25rem", // 20px
        paragraphMargin: "1.75rem", // 28px - Slightly reduced
        appPreviewMargin: "2rem 0", // 32px vertical - Reduced for consistency
        maxTitleWidth: "90%", // Percentage-based for better responsiveness
        maxParagraphWidth: "85%", // Percentage-based for better responsiveness
        headerPadding: "0 1.25rem", // 20px
        headerTop: "1.25rem", // 20px
        buttonContainerMaxWidth: "400px", // Slightly reduced for consistency
      };
    } else {
      // iPad Pro range (1024-1199px) - Large tablet layout
      // NOTE: Desktop (1200px+) uses DesktopHeroLayout, not this component
      return {
        titleSize: "3.25rem", // 52px - Reduced from 60px for better proportion
        paragraphSize: "1.5rem", // 24px - Reduced from 28px for better balance
        buttonSize: "1.25rem", // 20px - Reduced from 24px for consistency
        titleLineHeight: TYPOGRAPHY.LINE_HEIGHTS.TIGHT, // 1.296 for hero titles
        paragraphLineHeight: TYPOGRAPHY.LINE_HEIGHTS.NORMAL, // 1.5 for readability
        titleFontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD, // 600 - hero emphasis
        paragraphFontWeight: TYPOGRAPHY.WEIGHTS.LIGHT, // 300 - Light weight as specified
        textShadow: TEXT_SHADOWS.HERO_MOBILE_LIGHT, // Lighter shadow for larger screens
        mainPadding: "0 2.5rem", // 40px horizontal
        titleMargin: "1.25rem", // 20px - Reduced spacing
        paragraphMargin: "2rem", // 32px - Reduced spacing
        appPreviewMargin: "2.5rem 0", // 40px vertical - Reduced
        maxTitleWidth: "90%", // Percentage-based for better responsiveness
        maxParagraphWidth: "85%", // Percentage-based for better responsiveness
        headerPadding: "0 1.5rem", // 24px
        headerTop: "1.5rem", // 24px
        buttonContainerMaxWidth: "450px", // Slightly reduced
      };
    }
  };

  const {
    titleSize,
    paragraphSize,
    buttonSize,
    titleLineHeight,
    paragraphLineHeight,
    titleFontWeight,
    paragraphFontWeight,
    textShadow,
    mainPadding,
    titleMargin,
    paragraphMargin,
    appPreviewMargin,
    maxTitleWidth,
    maxParagraphWidth,
    headerPadding,
    headerTop,
    buttonContainerMaxWidth,
  } = getResponsiveSpecs();

  const handleJoinSuccess = (data) => {
    setUserEmail(data.email);
    setHasJoined(true);
    if (data.referral_code) {
      setReferrals((prev) => ({ ...prev, user: data.referral_code }));
    }
    // Call the parent's success handler
    if (onWaitlistSuccess) {
      onWaitlistSuccess(data);
    }
  };

  return (
    <div
      style={{
        width: "100%",
        minHeight: "100vh",
        backgroundImage: 'url("/tablet-hero-bg.jpg")',
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        display: "flex",
        flexDirection: "column",
        fontFamily: "Inter, sans-serif",
      }}
    >
      {/* Header */}
      <header
        style={{
          position: "absolute",
          top: headerTop,
          left: "0",
          right: "0",
          zIndex: 20,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: headerPadding,
        }}
      >
        <Badge variant="ffsLogo" size="desktop" />

        <Badge variant="earlyAccess" size="desktop" />
      </header>

      {/* Main Content */}
      <main
        style={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
          padding: mainPadding,
          paddingTop:
            windowSize?.width < BREAKPOINTS.TABLET_MD ? "6rem" : "4rem", // More space for iPad Mini header
          paddingBottom:
            windowSize?.width < BREAKPOINTS.TABLET_MD ? "2.5rem" : "4rem", // Increased bottom padding for iPad Mini
          overflow: "auto", // Allow scrolling if content doesn't fit
        }}
      >
        {/* Hero Title - Split into two lines per Figma design */}
        <h1
          style={{
            fontFamily: TYPOGRAPHY.FONTS.MONTSERRAT.join(", "),
            fontWeight: titleFontWeight,
            fontSize: titleSize,
            color: "white",
            lineHeight: titleLineHeight,
            marginBottom: titleMargin,
            maxWidth: maxTitleWidth,
            textShadow: textShadow,
            textAlign: "center",
          }}
        >
          Train Your Mind.
          <br />
          Dominate The Pitch.
        </h1>

        {/* Hero Paragraph */}
        <p
          style={{
            fontFamily: TYPOGRAPHY.FONTS.POPPINS.join(", "),
            fontWeight: paragraphFontWeight,
            fontSize: paragraphSize,
            color: "white",
            lineHeight: paragraphLineHeight,
            marginBottom: paragraphMargin,
            minWidth:
              windowSize?.width >= BREAKPOINTS.TABLET_MD &&
              windowSize?.width < BREAKPOINTS.TABLET_LG
                ? "542px"
                : "622px",
            maxWidth:
              windowSize?.width >= BREAKPOINTS.TABLET_MD &&
              windowSize?.width < BREAKPOINTS.TABLET_LG
                ? "542px"
                : "622px",
            textShadow: textShadow,
            textAlign: "center",
          }}
        >
          The meditation app built exclusively for footballers. Master your
          mindset, find flow fast and perform, when it matters most.
        </p>

        {/* App Preview / Referral Component - Key visual element from Figma */}
        <div
          style={{
            margin: appPreviewMargin,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            width: "100%",
          }}
        >
          <AppPreview />
        </div>

        {/* Scroll Down Button - Visual indicator to direct users to scroll */}
        {!hasJoined && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginBottom:
                windowSize?.width < BREAKPOINTS.TABLET_MD ? "1.5rem" : "2rem",
              animation: `bounce 2s infinite ease-in-out`,
            }}
          >
            <div
              style={{
                width: "52px",
                height: "52px",
                backgroundColor: "white",
                borderRadius: "18px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              <ChevronDownIcon size={24} color="#0D0E20" strokeWidth="1" />
            </div>
            <style>
              {`
                @keyframes bounce {
                  0%, 20%, 50%, 80%, 100% {
                    transform: translateY(0);
                  }
                  40% {
                    transform: translateY(-8px);
                  }
                  60% {
                    transform: translateY(-4px);
                  }
                }
              `}
            </style>
          </div>
        )}

        {/* Email Input and Button - Only show if not joined */}
        {!hasJoined && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              width: "100%",
              maxWidth: buttonContainerMaxWidth,
              alignItems: "center",
            }}
          >
            <button
              onClick={() => setShowEmailPopup(true)}
              style={{
                width: "100%",
                minWidth: "400px",
                maxWidth: "400px",
                height: "4rem", // 64px height as required
                backgroundColor: "white",
                color: "#0D0E20",
                border: "none",
                borderRadius: "1.25rem", // 20px
                fontSize: "1.5rem", // 24px Inter Semibold
                fontFamily: TYPOGRAPHY.FONTS.INTER.join(", "),
                fontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
                cursor: "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
                padding: "0 1rem", // Add horizontal padding for better text spacing
              }}
            >
              Join Waitlist
            </button>

            {/* Returning user viewing a friend's referral link */}
            {hasExistingSession && (
              <SessionSwitchLink onClick={onShowExisting} align="center" style={{ marginTop: 0 }}>
                Already joined? View your referral link
              </SessionSwitchLink>
            )}
          </div>
        )}

        {/* Success State Buttons - Show if joined */}
        {hasJoined && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "1rem",
              width: "100%",
              maxWidth: buttonContainerMaxWidth,
              alignItems: "center",
            }}
          >
          {showInlineCard && (
            <ShareCardInline
              referralLink={referralLink}
              referralCount={referralCount}
              onClose={() => setShowInlineCard(false)}
              deviceType="tablet"
            />
          )}
            {/* Joined Button - Dark with checkmark */}
            <button
              style={{
                width: "100%",
                minWidth:
                  windowSize?.width < BREAKPOINTS.TABLET_MD ? "280px" : "400px",
                maxWidth:
                  windowSize?.width < BREAKPOINTS.TABLET_MD ? "320px" : "400px",
                height: "4rem", // 64px height consistent with original button
                backgroundColor: "#0D0E20", // Dark background matching the design
                color: "white",
                border: "none",
                borderRadius: "1.25rem", // 20px - same as original button
                fontSize: buttonSize, // Responsive button text size
                fontFamily: TYPOGRAPHY.FONTS.INTER.join(", "),
                fontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
                cursor: "default", // Not clickable
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem", // 12px gap between icon and text
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
                transition: "all 0.2s ease",
              }}
            >
              <CheckIcon
                size={windowSize?.width < BREAKPOINTS.TABLET_MD ? 20 : 24}
                color="white"
                strokeWidth="2.5"
              />
              Joined
            </button>

            {/* Refer a Friend Button - Black with handshake icon */}
            <button
              onClick={handleShowReferral}
              style={{
                width: "100%",
                minWidth:
                  windowSize?.width < BREAKPOINTS.TABLET_MD ? "280px" : "400px",
                maxWidth:
                  windowSize?.width < BREAKPOINTS.TABLET_MD ? "320px" : "400px",
                height: "4rem", // 64px height consistent with other buttons
                backgroundColor: "#000000", // Pure black matching desktop
                color: "white",
                border: "none",
                borderRadius: "1.25rem", // 20px
                fontSize: buttonSize, // Responsive button text size
                fontFamily: TYPOGRAPHY.FONTS.INTER.join(", "),
                fontWeight: TYPOGRAPHY.WEIGHTS.SEMIBOLD,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "0.75rem", // 12px gap between elements
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.3)",
                transition: "all 0.2s ease",
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = "#333333"; // Darker on hover
                e.target.style.transform = "translateY(-2px)";
                e.target.style.boxShadow = "0 15px 35px rgba(0, 0, 0, 0.4)";
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = "#000000";
                e.target.style.transform = "translateY(0)";
                e.target.style.boxShadow = "0 10px 30px rgba(0, 0, 0, 0.3)";
              }}
            >
              <span
                style={{
                  fontSize:
                    windowSize?.width < BREAKPOINTS.TABLET_MD
                      ? "1.125rem"
                      : "1.25rem",
                }}
              >
                🏆
              </span>
              <span>Refer a Friend</span>
              <svg
                width={windowSize?.width < BREAKPOINTS.TABLET_MD ? 20 : 26}
                height={windowSize?.width < BREAKPOINTS.TABLET_MD ? 20 : 26}
                viewBox="0 0 26 26"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                  width:
                    windowSize?.width < BREAKPOINTS.TABLET_MD
                      ? "1.25rem"
                      : "1.625rem",
                  height:
                    windowSize?.width < BREAKPOINTS.TABLET_MD
                      ? "1.25rem"
                      : "1.625rem",
                  filter: "brightness(0) invert(1)", // White color
                }}
              >
                <rect
                  width="26"
                  height="26"
                  fill="url(#pattern0_more_tablet)"
                />
                <defs>
                  <pattern
                    id="pattern0_more_tablet"
                    patternContentUnits="objectBoundingBox"
                    width="1"
                    height="1"
                  >
                    <use href="#image0_more_tablet" transform="scale(0.01)" />
                  </pattern>
                  <image
                    id="image0_more_tablet"
                    width="100"
                    height="100"
                    preserveAspectRatio="none"
                    href="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAGQAAABkCAYAAABw4pVUAAAACXBIWXMAAAsTAAALEwEAmpwYAAABIklEQVR4nO3dMU7EMABFwT0SXIdut+IGtFycfQghCqSlzheZ6dxZebHTRPblAgAAAAAAwGGqJ49/RPVe3avXo+dyen3H+CHKkarn6qPfvsa307+pB0a5PohipRwpUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2ZMoexJlT6LsSZQ9ibInUfYkyp5E2VPd/vjB+3r03E6rxyvla/xy9NxOKUF2ZMvakY/6jsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHYuxIjB2JsSMxdiTGjsTYkRg7EmNHDuPfU705hH8zyt2FLkNy5REAAAAAAMDlv/kEAny3FazsZ1QAAAAASUVORK5CYII="
                  />
                </defs>
              </svg>
            </button>

            {/* Sign up another email in this browser (friend / testing) */}
            <SessionSwitchLink onClick={onUseDifferentEmail} align="center" style={{ marginTop: 0 }}>
              Not you? Use a different email
            </SessionSwitchLink>
          </div>
        )}
      </main>

      {/* Email Popup */}
      <EmailPopup
        isOpen={showEmailPopup}
        onClose={() => setShowEmailPopup(false)}
        onSuccess={handleJoinSuccess}
        referredBy={referredBy || referrals?.friend}
      />
    </div>
  );
};

export default TabletHeroLayout;
